import React, { useCallback, useState } from 'react'

import Snackbar from '@/shared/components/Snackbar/Snackbar.functions'
import { Box, Typography } from '@mui/material'

import Modal from '../../../../shared/components/Modal/Modal.component'
import USPSLocationFinder from '../../../../shared/components/USPSLocationFinder/USPSLocationFinder.component'

import {
  resetUpdateUspsLocation,
  updateUspsLocation
} from '../../../../redux/states/fulfillmentOrders/updateUspsLocation/updateUspsLocation.slice'
import type { UspsLocation } from '../../../../redux/states/usps/getUspsLocations/getUspsLocations.types'
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/redux.hooks'
import styles from './UspsLocationSelectionModal.styles'
import { UspsLocationSelectionModalProps } from './UspsLocationSelectionModal.types'

const UspsLocationSelectionModal: React.FC<UspsLocationSelectionModalProps> = (props) => {
  const { open, onClose, fulfillmentOrder, refreshFlightItineraries } = props

  const dispatch = useAppDispatch()
  const { loading: updateUspsLocationLoading } = useAppSelector((state) => state.updateUspsLocation)

  const [selectedLocation, setSelectedLocation] = useState<UspsLocation | null>(null)
  const [zipCodeForRequest, setZipCodeForRequest] = useState<string>(fulfillmentOrder.uspsZipCode ?? '')

  const handleLocationSelect = useCallback((_locationId: string, locationData: UspsLocation) => {
    setSelectedLocation(locationData)
    setZipCodeForRequest(locationData.zipCode)
  }, [])

  const handleConfirm = useCallback(() => {
    if (!selectedLocation) return
    dispatch(
      updateUspsLocation({
        request: {
          orderId: fulfillmentOrder.orderId,
          zipCode: zipCodeForRequest,
          locationId: selectedLocation.locationID,
          locationAddress: selectedLocation.address
        },
        onSuccess: () => {
          onClose()
          Snackbar.show({
            message: 'Location updated. A new label will be sent by email.',
            severity: 'success'
          })
          dispatch(resetUpdateUspsLocation())
          refreshFlightItineraries()
        }
      })
    )
  }, [dispatch, fulfillmentOrder.orderId, selectedLocation, zipCodeForRequest, onClose, refreshFlightItineraries])

  const handleClose = useCallback(() => {
    setSelectedLocation(null)
    setZipCodeForRequest(fulfillmentOrder.uspsZipCode ?? '')
    onClose()
  }, [fulfillmentOrder.uspsZipCode, onClose])

  const modalBody = (
    <Box>
      <Typography sx={styles.noteText}>
        You will receive a new shipping label via email after selecting the location.
      </Typography>
      <USPSLocationFinder
        recipientZipCode={fulfillmentOrder.uspsZipCode ?? ''}
        onLocationSelect={handleLocationSelect}
        selectedLocationID={selectedLocation?.locationID}
      />
    </Box>
  )

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Select USPS Drop-off Location"
      body={modalBody}
      primaryButton={{
        label: 'Confirm Selection',
        onClick: handleConfirm,
        loading: updateUspsLocationLoading,
        disabled: !selectedLocation || updateUspsLocationLoading
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: handleClose
      }}
    />
  )
}

export default UspsLocationSelectionModal
